import { useState, useCallback, useEffect, useRef } from "react";
import Cropper from "react-easy-crop";
import { getCroppedImg } from "../../utils/cropImage";

// El tamaño se configura por props o usa 1MB como valor predeterminado

export default function ImageUploaderWithCrop({ 
  onCropped, 
  aspectRatio = 1, 
  setFieldTouched,
  setFieldError,
    setFieldValue,
  maxFileSize = 1048576, // 1MB por defecto, configurable
  fieldName = "image",   // Nombre del campo en formik
  values,                // Valores actuales del formulario para detectar reset
  resetKey               // Prop que cambia cuando se resetea el formulario
}) {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [fileError, setFileError] = useState("");
  const [fileName, setFileName] = useState("");
  const fileInputRef = useRef(null);
  
  // Efecto para detectar reset del formulario
  useEffect(() => {
    if (resetKey) {
      resetCropper();
      // También limpiar errores en Formik
      setFieldError && setFieldError(fieldName, undefined);
    }
  }, [resetKey, fieldName, setFieldError]);

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const validateFileSize = (file) => {
    if (file.size > maxFileSize) {
      const sizeMB = maxFileSize / (1024 * 1024);
      return `El archivo es demasiado grande. El tamaño máximo es ${sizeMB}MB.`;
    }
    return null;
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Guardar el nombre del archivo
      setFileName(file.name);
      
      // Validar tamaño antes de procesar
      const error = validateFileSize(file);
      if (error) {
        setFileError(error);
        setFieldError && setFieldError(fieldName, error);
        setFieldTouched(fieldName, true, true);
        return;
      }

      setFileError("");
      // Notificar a Formik que este campo está en proceso pero no completado
      setFieldTouched(fieldName, true, false);
      // Indicar que el campo está siendo procesado pero aún no tiene valor válido
      setFieldValue && setFieldValue(fieldName, null);
      
      const reader = new FileReader();
      reader.onload = () => setImageSrc(reader.result);
      reader.readAsDataURL(file);
    } else {
      // Si no hay archivo seleccionado (por ejemplo, al cancelar el diálogo)
      setFileName("");
    }
  };

  const handleCrop = async () => {
    try {
      // Notificar que el campo ha sido tocado antes de procesar
      setFieldTouched(fieldName, true, false);
      
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
      
      // Validar tamaño del blob resultante
      if (croppedImage.size > maxFileSize) {
        const sizeMB = maxFileSize / (1024 * 1024);
        const error = `La imagen recortada es demasiado grande. El tamaño máximo es ${sizeMB}MB.`;
        setFileError(error);
        setFieldError && setFieldError(fieldName, error);
        setFieldTouched(fieldName, true, true);
        return;
      }
      
      onCropped(croppedImage); // Devuelve el blob al padre
      resetCropper(); // Limpia cropper luego de guardar
    } catch (e) {
      console.error("Error al recortar imagen:", e);
      setFileError("Error al procesar la imagen");
      setFieldError && setFieldError(fieldName, "Error al procesar la imagen");
      setFieldTouched(fieldName, true, true);
    }
  };

  const resetCropper = () => {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setFileError("");
    setFileName("");
    
    // Limpiar el campo de archivo
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-4">
      {!imageSrc && (
        <div className="space-y-2">
          <div className="flex items-center">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
              ref={fileInputRef}
              id={`file-upload-${fieldName}`}
            />
            <label 
              htmlFor={`file-upload-${fieldName}`}
              className="cursor-pointer bg-indigo-100 text-indigo-700 px-4 py-2 rounded hover:bg-indigo-200"
            >
              Seleccionar archivo
            </label>
            <span className="ml-3 text-sm text-gray-500">
              {fileName ? fileName : "Ningún archivo seleccionado"}
            </span>
          </div>
          {fileError && (
            <p className="text-red-500 text-sm">{fileError}</p>
          )}
        </div>
      )}

      {imageSrc && (
        <>
          <div className="relative w-full h-64 bg-gray-200 rounded overflow-hidden">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspectRatio}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>

          <div className="flex items-center gap-4">
            <label className="text-sm text-gray-600">Zoom</label>
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(+e.target.value)}
              className="w-full"
            />
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={resetCropper}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleCrop}
              className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
            >
              Guardar recorte
            </button>
          </div>
          
          {fileError && (
            <p className="text-red-500 text-sm mt-1">{fileError}</p>
          )}
        </>
      )}
    </div>
  );
}